/**
 * Hook for real-time volume candle updates via unified SSE
 *
 * Connects to the unified SSE endpoint for live ES volume candle streaming.
 * Volume candles complete based on traded volume rather than time.
 * Uses the shared UnifiedSSEClient singleton to avoid per-indicator connections.
 *
 * =============================================================================
 * USAGE
 * =============================================================================
 *
 * const { connected, error } = useLiveVolumeCandles(
 *   (candle, isComplete) => {
 *     // isComplete=true: Volume threshold reached, candle complete
 *     // isComplete=false: In-progress update
 *     handleCandleUpdate(candle, isComplete);
 *   },
 *   (candles, current) => {
 *     // Bulk historical candles + current in-progress candle
 *     initializeChart(candles, current);
 *   },
 *   5000 // volume per candle
 * );
 *
 * =============================================================================
 * SSE EVENT TYPES
 * =============================================================================
 *
 * 1. bulk_volume_candles - Sent immediately on connection
 *    { volume_size: 5000, data: [...candles], current: {...} }
 *
 * 2. volume_candle - Completed candle (volume threshold reached)
 *    { volume_size: 5000, data: {...} }
 *
 * 3. current_volume_candle - In-progress candle update
 *    { volume_size: 5000, data: {...} }
 *
 * =============================================================================
 */

import { useEffect, useRef, useState } from 'react';
import { unifiedSSE } from './useUnifiedSSE';

/**
 * Hook for live volume candle updates via unified SSE
 *
 * @param {Function} onCandleUpdate - Callback for candle updates (candle, isComplete)
 * @param {Function} onBulkCandles - Callback for bulk historical candles (candles, current)
 * @param {number} volumeSize - Volume per candle (default 5000)
 * @param {boolean} enabled - Whether to connect (default true)
 * @returns {Object} - Hook state and methods
 */
export function useLiveVolumeCandles(onCandleUpdate, onBulkCandles, volumeSize = 5000, enabled = true) {
  const onCandleUpdateRef = useRef(onCandleUpdate);
  const onBulkCandlesRef = useRef(onBulkCandles);
  const bulkReceivedRef = useRef(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    onCandleUpdateRef.current = onCandleUpdate;
    onBulkCandlesRef.current = onBulkCandles;
  }, [onCandleUpdate, onBulkCandles]);

  useEffect(() => {
    if (!enabled) {
      setConnected(false);
      return;
    }

    const channel = `es:vol_candles:${volumeSize}`;
    bulkReceivedRef.current = false;

    const handleBulk = (message) => {
      if (onBulkCandlesRef.current) {
        console.log('[LiveVolumeCandles] Received bulk candles:', message.data?.length || 0, 'current:', !!message.current);
        bulkReceivedRef.current = true;
        onBulkCandlesRef.current(message.data || [], message.current);
        setConnected(true);
        setError(null);
      }
    };

    const handleCandle = (message) => {
      if (onCandleUpdateRef.current && message.data && bulkReceivedRef.current) {
        console.log('[LiveVolumeCandles] Completed candle:', message.data.close, 'vol:', message.data.volume);
        onCandleUpdateRef.current(message.data, true);
      }
    };

    const handleCurrentCandle = (message) => {
      if (onCandleUpdateRef.current && message.data && bulkReceivedRef.current) {
        onCandleUpdateRef.current(message.data, false);
      }
    };

    const unsub1 = unifiedSSE.subscribe(channel, 'bulk_volume_candles', handleBulk);
    const unsub2 = unifiedSSE.subscribe(channel, 'volume_candle', handleCandle);
    const unsub3 = unifiedSSE.subscribe(channel, 'current_volume_candle', handleCurrentCandle);

    console.log(`[LiveVolumeCandles] Subscribed to unified SSE channel: ${channel}`);

    return () => {
      unsub1();
      unsub2();
      unsub3();
      setConnected(false);
      console.log(`[LiveVolumeCandles] Unsubscribed from unified SSE channel: ${channel}`);
    };
  }, [volumeSize, enabled]);

  return { connected, error };
}

export default useLiveVolumeCandles;
